import { useState, useEffect } from 'react';
import { useLanguage } from '../../helpers/language-context';
import { supabase } from '../../supabaseClient';
import ApplicationModal from './application-modal';
import Toast from '../../components/common/Toast';

const JobOpeningList = () => {
  const [activeFilter, setActiveFilter] = useState(null);
  const { language, translations } = useLanguage();
  const [jobs, setJobs] = useState([]);

  const [selectedJob, setSelectedJob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    const fetchJobs = async () => {
      const { data, error } = await supabase
        .from('job_openings')
        .select('*')
        .eq('status', true)
        .order('id', { ascending: true });

      if (!error) {
        setJobs(data);
      }

    };

    fetchJobs();
  }, []);

  const getLocalized = (job, key) => {
    if (language === 'ms') return job[`${key}-ms`] ?? job[key];
    return job[key];
  };

  const handleApply = (job) => {
    setSelectedJob(job);
  };

  const handleCloseModal = () => {
    setSelectedJob(null);
  };

  const handleSubmitApplication = async (formData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('career_applications')
        .insert([{
          job_id: selectedJob.id,
          job_title: selectedJob.title, // Store snapshot of title
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          resume_url: formData.resumeUrl,
          linkedin_url: formData.linkedinUrl,
          message_to_hirer: formData.messageToHirer
        }]);

      if (error) throw error;

      setToast({ show: true, message: translations?.careerForm?.success || 'Application submitted successfully!', type: 'success' });
      handleCloseModal();
    } catch (error) {
      console.error('Error submitting application:', error);
      setToast({ show: true, message: translations?.careerForm?.error || 'Failed to submit application. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };


  const categories = Array.from(
    new Set(
      jobs
        .map((job) => getLocalized(job, 'category'))
        .filter(Boolean)
    )
  );

  const viewAllLabel = language === 'ms' ? 'Lihat Semua' : 'View All';
  const filters = [{ label: viewAllLabel, value: null }, ...categories.map((c) => ({ label: c, value: c }))];

  const filteredJobs = activeFilter === null
    ? jobs
    : jobs.filter((job) => getLocalized(job, 'category') === activeFilter);

  return (
    <section>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
      {/* Header Section */}
      <div className="mb-12">
        <h1 className="text-4xl md:text-5xl text-[#28357B] mb-6">
          {translations?.career?.title || 'Be Part of Our Team'}
        </h1>
        <p className="text-gray-600 max-w-3xl leading-relaxed">
          {translations?.career?.description || 'Love working on cutting-edge projects? Join our team and be a part of our exciting journey!'}
        </p>
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-3 mb-12 border-b-2 border-gray-200 pb-8">
        {filters.map((filter) => (
          <button
            key={`${filter.label}-${filter.value ?? 'all'}`}
            onClick={() => setActiveFilter(filter.value)}
            className={`px-6 py-2.5 rounded-full font-medium transition-all duration-200 ${activeFilter === filter.value
              ? 'bg-[#2d3e7e] text-white shadow-md'
              : 'bg-white text-[#2d3e7e] border-2 border-[#2d3e7e] hover:bg-gray-50'
              }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Job Listings */}
      <div className="space-y-8">
        {filteredJobs.map((job, index) => (
          <div
            key={index}
            className="border-b-2 border-gray-200 pb-8"
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 group cursor-pointer">
              <div className="flex-1">
                <h2 className="text-2xl mb-4 text-[#28357B]">
                  {getLocalized(job, 'title')}
                </h2>
                <p className="mb-6 leading-relaxed">
                  {getLocalized(job, 'description')}
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span>{getLocalized(job, 'time')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeWidth="2" />
                      <circle cx="12" cy="10" r="3" strokeWidth="2" />
                    </svg>
                    <span>{getLocalized(job, 'location')}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleApply(job)}
                className="flex items-center gap-2 whitespace-nowrap group text-[#28357B] group-hover:text-[#FDA729]">
                {translations?.button?.apply || 'Apply'}
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M5 12h14M12 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
      {selectedJob && (
        <ApplicationModal
          job={selectedJob}
          onClose={handleCloseModal}
          onSubmit={handleSubmitApplication}
          isSubmitting={isSubmitting}
        />
      )}
    </section>
  );
}

export default JobOpeningList;
